/**
 * /frontend/src/features/analytics/components/shared/DateRangeFilter.js
 */

import React from 'react';
import { DATE_PRESETS } from '../../constants/datePresets';
import { compareIsoDates, isIsoDateString } from '../../utils/dateUtils';
import './DateRangeFilter.css';

const minDate = (a, b) => (a && b ? (compareIsoDates(a, b) <= 0 ? a : b) : (a || b || ''));

const maxDate = (a, b) => (a && b ? (compareIsoDates(a, b) >= 0 ? a : b) : (a || b || ''));

const PRESET_ORDER = ['today', 'yesterday', '7d', '30d'];

const PRESET_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': '7 days',
  '30d': '1 month'
};

/**
 * Date range filter with presets and custom inputs.
 * @param {{startDate:string,endDate:string,presetKey:string,onPresetChange?:Function,onStartDateChange?:Function,onEndDateChange?:Function,onCustomClick?:Function,minDate?:string,maxDate?:string,error?:string,showCustomFields?:boolean,disabled?:boolean}} props
 * @returns {JSX.Element}
 */
const DateRangeFilter = ({
  startDate,
  endDate,
  presetKey,
  onPresetChange = () => {},
  onStartDateChange = () => {},
  onEndDateChange = () => {},
  onCustomClick = null,
  minDate: minAllowed,
  maxDate: maxAllowed,
  error,
  showCustomFields = true,
  disabled = false
}) => (
  <div className="analytics-date-range">
    <div className="analytics-date-preset-bar" role="group" aria-label="Analytics date range presets">
      {PRESET_ORDER.map((key) => {
        const preset = DATE_PRESETS.find((item) => item.key === key);
        if (!preset) return null;
        return (
          <button
            key={preset.key}
            type="button"
            className={`analytics-date-preset-chip ${presetKey === preset.key ? 'active' : ''}`}
            onClick={() => onPresetChange(preset.key)}
            disabled={disabled}
          >
            {PRESET_LABELS[preset.key] || preset.label}
          </button>
        );
      })}
      <button
        type="button"
        className={`analytics-date-preset-chip ${presetKey === 'custom' ? 'active' : ''}`}
        onClick={() => {
          if (typeof onCustomClick === 'function') {
            onCustomClick();
            return;
          }
          onPresetChange('custom');
        }}
        disabled={disabled}
      >
        Custom
      </button>
    </div>

    {showCustomFields && presetKey === 'custom' ? (
      <div className="analytics-date-custom-fields">
        <div className="analytics-date-field">
          <label htmlFor="analytics-start-date">Start</label>
          <input
            id="analytics-start-date"
            type="date"
            value={startDate}
            min={isIsoDateString(minAllowed) ? minAllowed : undefined}
            max={isIsoDateString(maxAllowed) ? minDate(endDate, maxAllowed) : undefined}
            onChange={(event) => onStartDateChange(event.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="analytics-date-field">
          <label htmlFor="analytics-end-date">End</label>
          <input
            id="analytics-end-date"
            type="date"
            value={endDate}
            min={isIsoDateString(minAllowed) ? maxDate(startDate, minAllowed) : startDate || undefined}
            max={isIsoDateString(maxAllowed) ? maxAllowed : undefined}
            onChange={(event) => onEndDateChange(event.target.value)}
            disabled={disabled}
          />
        </div>
      </div>
    ) : null}

    {error ? <div className="analytics-date-error">{error}</div> : null}
  </div>
);

export default DateRangeFilter;
